<template>
  <div>
    <div
      class="mb-8 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 px-3 py-3"
    >
      <i class="bi bi-stars text-primary text-base shrink-0 mt-0.5" />

      <p class="text-sm text-foreground/80 leading-relaxed p-0 mt-0.5">
        <span class="font-semibold text-foreground">
          93% of shoppers say visuals help decide the purchase
        </span>
        — closing the gaps below lifts conversion.
      </p>
    </div>
    <div class="tw-report flex items-center justify-between gap-3 mb-3 flex-wrap text-slate-700">
      <h3 class="text-sm font-semibold text-foreground">
        {{ title }}
      </h3>

      <div class="inline-flex items-center gap-2 text-[11px]">
        <button
          v-if="user_id"
          id="aiPreviewBtn"
          class="iframe-action-btn"
          title="AI Edit"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
            <path d="M20 3v4" />
            <path d="M22 5h-4" />
          </svg>
          <span>AI Edit</span>
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors"
          :class="localShowMobileLegibility
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-slate-200 bg-white text-slate-500 hover:text-slate-900'"
          @click="localShowMobileLegibility = !localShowMobileLegibility"
        >
          <i class="bi bi-phone text-xs" />
          Mobile legibility
        </button>
        <span class="text-muted-foreground font-medium">Order:</span>

        <div class="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 p-1 font-medium shadow-sm">
          <button
            type="button"
            class="appearance-none border-0 px-3 py-1.5 rounded-full text-sm font-semibold leading-none transition-colors focus:outline-none focus:ring-0"
            :class="!localShowRecommendedOrder
              ? 'bg-white text-slate-900 shadow-sm'
              : 'bg-transparent text-slate-500 hover:text-slate-900'"
            @click="localShowRecommendedOrder = false"
          >
            Current
          </button>

          <button
            type="button"
            class="appearance-none border-0 px-3 py-1.5 rounded-full text-sm font-semibold leading-none transition-colors focus:outline-none focus:ring-0"
            :class="localShowRecommendedOrder
              ? 'bg-white text-slate-900 shadow-sm'
              : 'bg-transparent text-slate-500 hover:text-slate-900'"
            @click="localShowRecommendedOrder = true"
          >
            Recommended
          </button>
        </div>
      </div>
    </div>

    <div class="relative">
      <div class="overflow-x-auto pb-3 mx-3">
        <div class="grid grid-flow-col auto-cols-[170px] gap-3 text-slate-700">
          <div
            v-for="(slot, index) in displayImageOrder"
            :key="makeSlotKey(slot, index)"
            class="group relative"
          >
            <div
              role="button"
              :aria-disabled="!isDisplaySlotClickable(slot, index)"
              :tabindex="isDisplaySlotClickable(slot, index) ? 0 : -1"
              class="w-full aspect-square rounded-lg overflow-hidden flex items-center justify-center relative transition-all text-left"
              :class="getDisplaySlotButtonClass(slot, index)"
              @click="handleDisplaySlotClick(slot, index)"
              @keydown.enter.prevent="handleDisplaySlotClick(slot, index)"
              @keydown.space.prevent="handleDisplaySlotClick(slot, index)"
            >
              <template v-if="slot.ai_src">
                <img
                  :src="slot.ai_src"
                  :alt="slot.type"
                  class="w-full h-full object-contain"
                >
              </template>

              <template v-else-if="slot.src && (slot.status === 'present' || slot.status === 'improve')">
                <img
                  :src="slot.src"
                  :alt="slot.type"
                  class="w-full h-full object-contain"
                >
              </template>

              <template v-else-if="isDisplaySlotLocked(slot, index)">
                <div class="flex flex-col items-center gap-1 px-2 text-center">
                  <i class="bi bi-lock text-primary/60 text-sm" />

                  <span class="text-[10px] font-semibold text-foreground leading-tight">
                    Recommended
                  </span>

                  <span class="text-[9px] text-muted-foreground leading-tight">
                    Unlock preview
                  </span>
                </div>
              </template>

              <template v-else-if="slot.status === 'missing' || slot.status === 'improve'">
                <div class="flex flex-col items-center gap-1 px-2 text-center">
                  <i class="bi bi-stars text-primary text-base" />

                  <span class="text-[10px] font-semibold text-primary leading-tight">
                    {{ slot.status === 'improve' ? 'Preview upgrade' : 'Preview with AI' }}
                  </span>
                </div>
              </template>

              <div
                v-if="isDisplaySlotGenerating(slot, index)"
                class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 px-3 text-center bg-white/80 backdrop-blur-[2px]"
              >
                <i class="bi bi-arrow-repeat text-primary text-lg animate-spin" />

                <span class="text-[10px] font-semibold text-primary leading-tight">
                  {{ currentGenerationStage.label }}
                </span>

                <div class="w-[82%] h-1.5 rounded-full bg-primary/15 overflow-hidden">
                  <div
                    class="h-full bg-primary transition-all duration-200 ease-out"
                    :style="{ width: Math.round(generationProgress * 100) + '%' }"
                  />
                </div>

                <span class="text-[9px] text-muted-foreground leading-tight">
                  {{ Math.round(generationProgress * 100) }}%
                </span>
              </div>

              <span
                class="absolute z-30 top-1.5 left-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-background/90 border border-border"
                :class="slot.status === 'present' || slot.status === 'improve' ? 'text-foreground' : 'text-muted-foreground'"
              >
                {{ index + 1 }}
              </span>

              <i
                v-if="slot.status === 'present' && !slot.ai_src"
                class="bi bi-check-circle-fill absolute z-30 top-1.5 right-1.5 text-emerald-500 bg-background rounded-full text-sm"
              />

              <button
                v-if="slot.status === 'improve' && !slot.ai_src && generatingSlot !== getGenerationSlotIndex(slot, index)"
                type="button"
                class="absolute z-40 top-1.5 right-1.5 inline-flex items-center gap-0.5 h-5 px-1.5 rounded text-[9px] text-white border-0 transition-colors"
                :class="isDisplayImproveButtonDisabled(slot, index)
                  ? 'bg-amber-300 cursor-not-allowed opacity-80'
                  : 'bg-amber-500 hover:bg-amber-600 cursor-pointer'"
                :disabled="isDisplayImproveButtonDisabled(slot, index)"
                :title="getDisplayImproveTitle(slot, index)"
                @click.stop.prevent="handleImproveClick(slot, index)"
              >
                <i class="bi bi-arrow-up-right text-[9px]" />
                Improve
              </button>
              <span
                v-if="slot.ai_src"
                class="absolute z-30 top-1.5 right-1.5 inline-flex items-center gap-0.5 h-5 px-1.5 rounded text-[9px] bg-primary text-primary-foreground border-0 text-white"
              >
                <i class="bi bi-stars text-[9px]" />
                AI
              </span>

              <div
                v-if="shouldShowMobileLegibilityBadge(slot)"
                class="mobile-legibility-badge absolute z-41 right-1.5 bottom-1.5"
                @click.stop
              >
                <button
                  type="button"
                  class="mobile-legibility-badge__btn"
                  :class="getMobileLegibilityBadgeClass(slot)"
                  :aria-label="getMobileLegibilityLabel(slot)"
                >
                  <!-- Ready: green phone with full check -->
                  <svg
                    v-if="getMobileLegibilityStatus(slot) === 'ready'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    class="h-5 w-5"
                    aria-hidden="true"
                  >
                    <rect
                      x="7"
                      y="3"
                      width="10"
                      height="18"
                      rx="2.4"
                      stroke="currentColor"
                      stroke-width="2"
                    />
                    <path
                      d="M10 6.5H14"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <circle
                      cx="12"
                      cy="17.6"
                      r="0.9"
                      fill="currentColor"
                    />
                    <path
                      d="M5.4 12.1L9.6 16.3L18.9 7"
                      stroke="currentColor"
                      stroke-width="2.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <!-- Needs work: orange phone with full slash -->
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    class="h-5 w-5"
                    aria-hidden="true"
                  >
                    <rect
                      x="7"
                      y="3"
                      width="10"
                      height="18"
                      rx="2.4"
                      stroke="currentColor"
                      stroke-width="2"
                    />
                    <path
                      d="M10 6.5H14"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                    <circle
                      cx="12"
                      cy="17.6"
                      r="0.9"
                      fill="currentColor"
                    />
                    <path
                      d="M5 5L19 19"
                      stroke="currentColor"
                      stroke-width="2.8"
                      stroke-linecap="round"
                    />
                    <path
                      d="M19 5L5 19"
                      stroke="currentColor"
                      stroke-width="2.7"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>

                <div class="mobile-legibility-tooltip">
                  <div class="mobile-legibility-tooltip__title">
                    {{ getMobileLegibilityLabel(slot) }}
                  </div>

                  <div
                    v-if="getMobileLegibilityReason(slot)"
                    class="mobile-legibility-tooltip__text"
                  >
                    {{ getMobileLegibilityReason(slot) }}
                  </div>
                </div>
              </div>

              <a
                v-if="getDisplaySlotDownloadUrl(slot, index)"
                href="#"
                class="absolute z-40 right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/90 border border-slate-200 text-slate-700 shadow-sm opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 hover:bg-white hover:text-slate-950 transition-all"
                :class="shouldShowMobileLegibilityBadge(slot)
                  ? 'bottom-9'
                  : 'bottom-1.5'"
                title="Download image"
                @click.stop.prevent="downloadDisplaySlotImage(slot, index)"
              >
                <i class="bi bi-download text-xs" />
              </a>
            </div>

            <div class="mt-2">
              <template v-if="localShowRecommendedOrder">
                <p class="text-[11px] font-semibold text-foreground leading-tight p-0 m-0">
                  {{ slot.type }}
                </p>

                <p class="text-[10px] text-muted-foreground leading-snug mt-0.5 p-0 m-0">
                  {{ slot.intent }}
                </p>

                <p
                  v-if="slot.why_it_matters"
                  class="text-[9px] text-muted-foreground/80 leading-snug mt-1 italic p-0 m-0"
                >
                  {{ slot.why_it_matters }}
                </p>
              </template>

              <template v-else>
                <p
                  v-if="slot.status === 'improve' && slot.improve_reason"
                  class="text-[10px] text-amber-700 leading-snug p-0 m-0 mt-0.5"
                >
                  {{ slot.improve_reason }}
                </p>

                <p
                  v-if="slot.recommended_slot && slot.recommended_slot !== index + 1"
                  class="text-[9px] text-blue-700 leading-snug p-0 m-0 mt-1"
                >
                  Recommended position: #{{ slot.recommended_slot }}
                </p>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent" />
    </div>

    <div
      v-if="showPreviewCta"
      class="mt-4 flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-2 flex-wrap"
    >
      <div class="flex items-center gap-2 text-xs text-muted-foreground">
        <i class="bi bi-lock text-sm shrink-0" />

        <span>
          <span class="font-semibold text-foreground">
            {{ freePreviewUsed ? 'AI preview used' : '1 free AI preview' }}
          </span>
          {{ freePreviewUsed ? usedPreviewText : unusedPreviewText }}
        </span>
      </div>

      <a
        :href="contactHref"
        class="inline-flex items-center justify-center gap-1.5 h-7 px-3 rounded-md border border-border bg-background text-xs font-medium hover:bg-muted shrink-0"
        target="_blank"
      >
        <i class="bi bi-stars text-xs" />
        {{ contactLabel }}
      </a>
    </div>
    <transition name="optix-lightbox-fade">
      <div
        v-if="lightboxImage"
        class="optix-lightbox"
        role="dialog"
        aria-modal="true"
        @click.self="closeLightbox"
      >
        <button
          type="button"
          class="optix-lightbox__close"
          aria-label="Close image preview"
          @click="closeLightbox"
        >
          <i class="bi bi-x-lg" />
        </button>

        <div class="optix-lightbox__content">
          <div class="optix-lightbox__image-wrap">
            <img
              :src="lightboxImage"
              :alt="lightboxTitle || 'Image preview'"
              class="optix-lightbox__image"
            >
          </div>

          <div
            v-if="lightboxTitle || lightboxSubtitle"
            class="optix-lightbox__caption"
          >
            <div
              v-if="lightboxTitle"
              class="optix-lightbox__title"
            >
              {{ lightboxTitle }}
            </div>

            <div
              v-if="lightboxSubtitle"
              class="optix-lightbox__subtitle"
            >
              {{ lightboxSubtitle }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const GENERATION_DURATION_MS = 35000;

const GENERATION_STAGES = [
  { at: 0, label: 'Analyzing your product…' },
  { at: 0.18, label: 'Studying the hero image…' },
  { at: 0.38, label: 'Composing the scene…' },
  { at: 0.62, label: 'Rendering visual…' },
  { at: 0.85, label: 'Polishing details…' },
];

export default {
  name: 'ReportImageCarousel',

  props: {
    title: {
      type: String,
      default: 'Image carousel',
    },

    reportData: {
      type: Object,
      default: () => ({}),
    },

    reportId: {
      type: [String, Number],
      default: null,
    },

    reportUuid: {
      type: String,
      default: null,
    },

    fetchReportOnMount: {
      type: Boolean,
      default: true,
    },

    product: {
      type: Object,
      default: () => ({}),
    },

    images: {
      type: Array,
      default: () => [],
    },

    currentImagesOrder: {
      type: Array,
      default: () => [],
    },

    recommendedImagesOrder: {
      type: Array,
      default: () => [],
    },

    carouselSlots: {
      type: Array,
      default: () => [],
    },

    imageRecommendations: {
      type: Array,
      default: () => [],
    },

    sourceImageSelection: {
      type: Object,
      default: () => ({
        image_index: null,
      }),
    },

    aiImages: {
      type: Array,
      default: () => [],
    },

    defaultRecommendedOrder: {
      type: Boolean,
      default: false,
    },

    showPreviewCta: {
      type: Boolean,
      default: true,
    },

    selectedService: {
      type: String,
      default: 'gemini_nano_banana_pro_image_generate',
    },

    contactHref: {
      type: String,
      default: 'https://itsrapid.ai/pdp-audits/',
    },

    contactLabel: {
      type: String,
      default: 'Contact us',
    },

    usedPreviewText: {
      type: String,
      default: 'on this report — contact us to generate more production-ready images.',
    },

    unusedPreviewText: {
      type: String,
      default: 'on this report — need more or want production-ready images?',
    },
    sourceImageIndex: {
      type: [Number, String],
      default: null,
    },
    showMobileLegibility: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      remoteReportData: null,
      isReportLoading: false,
      localShowRecommendedOrder: this.defaultRecommendedOrder,
      localAiImages: [],
      generatedSlots: {},
      generatingSlot: null,
      generationProgress: 0,
      generationTimer: null,
      generationFinishTimer: null,
      localShowMobileLegibility: this.showMobileLegibility,
      lightboxImage: null,
      lightboxTitle: null,
      lightboxSubtitle: null,
      user_id: window.user_id
    };
  },

  computed: {
    resolvedReportData() {
      return {
        ...(this.remoteReportData || {}),
        ...(this.reportData || {}),
      };
    },

    resolvedProduct() {
      return {
        ...(this.resolvedReportData.product || {}),
        ...(this.product || {}),
      };
    },

    resolvedImages() {
      if (this.images.length) {
        return this.images;
      }

      return Array.isArray(this.resolvedReportData.images)
        ? this.resolvedReportData.images
        : [];
    },

    resolvedImageRecommendations() {
      if (this.imageRecommendations.length) {
        return this.imageRecommendations;
      }

      return Array.isArray(this.resolvedReportData.image_recommendations)
        ? this.resolvedReportData.image_recommendations
        : [];
    },

    resolvedSourceImageSelection() {
      if (
        this.sourceImageSelection
        && this.sourceImageSelection.image_index !== null
        && this.sourceImageSelection.image_index !== undefined
      ) {
        return this.sourceImageSelection;
      }

      return this.resolvedReportData.source_image_selection || { image_index: null };
    },

    aiSourceImage() {
      const explicitIndex = Number(this.sourceImageIndex);

      if (Number.isInteger(explicitIndex) && explicitIndex >= 0) {
        return this.resolvedImages[explicitIndex] || this.resolvedImages[0] || null;
      }

      const reportIndex = Number.isInteger(this.resolvedSourceImageSelection.image_index)
        ? this.resolvedSourceImageSelection.image_index
        : 0;

      return this.resolvedImages[reportIndex] || this.resolvedImages[0] || null;
    },

    savedAiImageMap() {
      return this.localAiImages.reduce((map, item) => {
        if (Number.isInteger(item.index) && item.src) {
          map[item.index] = this.normalizeUrlWithCurrentHost(item.src);
        }

        return map;
      }, {});
    },

    freePreviewUsed() {
      if (this.resolvedReportData?.premium_report) {
        return false;
      }

      return this.localAiImages.length > 0 || Object.keys(this.generatedSlots).length > 0;
    },

    currentGenerationStage() {
      const reversedStages = [...GENERATION_STAGES].reverse();

      return reversedStages.find((stage) => this.generationProgress >= stage.at) || GENERATION_STAGES[0];
    },

    currentImageOrder() {
      const rows = this.currentImagesOrder.length
        ? this.currentImagesOrder
        : (
          Array.isArray(this.resolvedReportData.current_images_order)
            ? this.resolvedReportData.current_images_order
            : []
        );

      if (rows.length) {
        return rows.map((item, index) => {
          const imageIndex = Number.isInteger(item.image_index)
            ? item.image_index
            : index;

          const recommendedSlotIndex = Number.isInteger(item.recommended_slot)
            ? item.recommended_slot - 1
            : index;

          return {
            ...item,
            view_type: 'current',
            display_index: index,
            image_index: imageIndex,
            status: ['present', 'improve'].includes(item.status) ? item.status : 'present',
            type: item.type || `Image ${index + 1}`,
            slot_type: item.slot_type || `Image ${index + 1}`,
            src: this.resolvedImages[imageIndex] || null,
            ai_src: this.savedAiImageMap[recommendedSlotIndex] || this.generatedSlots[recommendedSlotIndex] || null,
            mobile_legibility: this.normalizeMobileLegibility(item.mobile_legibility),
          };
        });
      }

      return this.resolvedImages.map((src, index) => ({
        view_type: 'current',
        display_index: index,
        image_index: index,
        status: 'present',
        recommended_slot: null,
        improve_reason: null,
        type: `Image ${index + 1}`,
        slot_type: `Image ${index + 1}`,
        src,
        ai_src: this.savedAiImageMap[index] || this.generatedSlots[index] || null,
        mobile_legibility: this.normalizeMobileLegibility(null),
      }));
    },

    recommendedImageOrder() {
      const slots = this.recommendedImagesOrder.length
        ? this.recommendedImagesOrder
        : (
          Array.isArray(this.resolvedReportData.recommended_images_order)
          && this.resolvedReportData.recommended_images_order.length
            ? this.resolvedReportData.recommended_images_order
            : this.carouselSlots
        );

      return this.normalizeRecommendedSlots(slots);
    },

    displayImageOrder() {
      return this.localShowRecommendedOrder
        ? this.recommendedImageOrder
        : this.currentImageOrder;
    },

    isAuthorized() {
      return Boolean(this.user_id);
    },
  },

  created() {
    this.localAiImages = this.aiImages.length
      ? [...this.aiImages]
      : (
        Array.isArray(this.resolvedReportData.ai_images)
          ? [...this.resolvedReportData.ai_images]
          : []
      );

    this.fetchReportData();
  },

  beforeDestroy() {
    this.clearGenerationTimers();
    this.closeLightbox();
  },

  methods: {
    shouldShowMobileLegibilityBadge(slot) {
      if (!this.localShowMobileLegibility) {
        return false;
      }

      if (!slot) {
        return false;
      }

      if (this.getMobileLegibilityStatus(slot) === 'unknown') {
        return false;
      }

      // Do not show mobile legibility for generated AI images.
      if (slot.ai_src) {
        return false;
      }

      // Do not show mobile legibility for missing / placeholder slots.
      if (slot.status === 'missing') {
        return false;
      }

      // Show only for real original PDP images.
      return Boolean(slot.src);
    },
    handleDisplaySlotClick(slot, displayIndex) {
      const imageUrl = this.getDisplaySlotPreviewUrl(slot, displayIndex);

      if (imageUrl) {
        this.openLightbox(slot, displayIndex);
        return;
      }

      if (!this.isDisplaySlotGeneratable(slot, displayIndex)) {
        return;
      }

      this.handleGenerate(this.getGenerationSlotIndex(slot, displayIndex));
    },

    getDisplaySlotPreviewUrl(slot, displayIndex) {
      if (this.isDisplaySlotGenerating(slot, displayIndex)) {
        return null;
      }

      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const recommendedSlot = this.recommendedImageOrder[generationSlotIndex] || slot;

      return (
        slot.ai_src
        || recommendedSlot.ai_src
        || slot.src
        || recommendedSlot.src
        || null
      );
    },

    openLightbox(slot, displayIndex) {
      const src = this.getDisplaySlotPreviewUrl(slot, displayIndex);

      if (!src) {
        return;
      }

      this.lightboxImage = src;
      this.lightboxTitle = slot.slot_type || slot.type || `Image ${displayIndex + 1}`;

      this.lightboxSubtitle = localStorage.getItem('optix-debug-lightbox') === '1'
        ? src
        : (
          slot.intent
          || slot.improve_reason
          || slot.why_it_matters
          || null
        );

      this.$nextTick(() => {
        document.body.classList.add('optix-lightbox-open');
        window.addEventListener('keydown', this.handleLightboxKeydown);
      });
    },

    closeLightbox() {
      this.lightboxImage = null;
      this.lightboxTitle = null;
      this.lightboxSubtitle = null;

      document.body.classList.remove('optix-lightbox-open');
      window.removeEventListener('keydown', this.handleLightboxKeydown);
    },

    handleLightboxKeydown(event) {
      if (event.key === 'Escape') {
        this.closeLightbox();
      }
    },

    getDisplaySlotDownloadUrl(slot, displayIndex) {
      if (this.isDisplaySlotGenerating(slot, displayIndex)) {
        return null;
      }

      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const recommendedSlot = this.recommendedImageOrder[generationSlotIndex] || slot;

      return (
        slot.ai_src
        || recommendedSlot.ai_src
        || slot.src
        || recommendedSlot.src
        || null
      );
    },

    async downloadDisplaySlotImage(slot, displayIndex) {
      const src = this.getDisplaySlotDownloadUrl(slot, displayIndex);

      if (!src) {
        this.notify('No image found', 'Cannot download this image.');
        return;
      }

      const filename = this.makeDownloadFilename(slot, displayIndex);

      try {
        const response = await fetch(src, {
          mode: 'cors',
        });

        if (!response.ok) {
          throw new Error('Could not download image.');
        }

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.warn('Direct image download failed, opening image instead:', error);

        const link = document.createElement('a');
        link.href = src;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    },

    makeDownloadFilename(slot, displayIndex) {
      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const slotNumber = generationSlotIndex + 1;
      const rawName = slot.slot_type || slot.type || `image-${slotNumber}`;

      const safeName = String(rawName)
          .toLowerCase()
          .replace(/[^a-z0-9]+/gi, '-')
          .replace(/^-+|-+$/g, '')
        || `image-${slotNumber}`;

      return `optix-${slotNumber}-${safeName}.png`;
    },
    isDisplaySlotGenerating(slot, displayIndex) {
      return this.generatingSlot === this.getGenerationSlotIndex(slot, displayIndex);
    },
    async fetchReportData() {
      if (!this.fetchReportOnMount) {
        return;
      }

      const { id, uuid } = this.getReportIdentity();

      if (!id || !uuid) {
        return;
      }

      this.isReportLoading = true;

      try {
        const response = await axios.get(`/analysis-reports/${id}/${uuid}/json`);

        this.remoteReportData = response.data?.report
          || response.data?.data
          || response.data
          || null;

        if (Array.isArray(this.remoteReportData?.ai_images)) {
          this.localAiImages = [...this.remoteReportData.ai_images];
        }
      } catch (error) {
        console.warn('Could not fetch full report data for carousel:', error);
      } finally {
        this.isReportLoading = false;
      }
    },

    normalizeRecommendedSlots(slots) {
      const currentStatusByImageIndex = this.currentImageOrder.reduce((map, item) => {
        if (Number.isInteger(item.image_index)) {
          map[item.image_index] = {
            status: item.status,
            improve_reason: item.improve_reason || null,
            mobile_legibility: item.mobile_legibility || null,
          };
        }

        return map;
      }, {});

      return slots.map((slot, index) => {
        const imageIndex = Number.isInteger(slot.image_index)
          ? slot.image_index
          : null;

        const currentImageMeta = imageIndex !== null
          ? currentStatusByImageIndex[imageIndex]
          : null;

        let normalizedStatus = ['present', 'improve', 'missing'].includes(slot.status)
          ? slot.status
          : 'missing';

        if (
          normalizedStatus === 'present'
          && currentImageMeta?.status === 'improve'
        ) {
          normalizedStatus = 'improve';
        }

        const savedAiSrc = this.savedAiImageMap[index] || null;
        const generatedAiSrc = this.generatedSlots[index] || null;

        return {
          ...slot,
          view_type: 'recommended',
          display_index: index,
          status: normalizedStatus,
          type: slot.slot_type || slot.type || 'Image slot',
          slot_type: slot.slot_type || slot.type || 'Image slot',
          improve_reason: slot.improve_reason || currentImageMeta?.improve_reason || null,
          ai_src: savedAiSrc || generatedAiSrc,
          src: ['present', 'improve'].includes(normalizedStatus) && imageIndex !== null
            ? this.resolvedImages[imageIndex] || null
            : null,
          mobile_legibility: this.normalizeMobileLegibility(
            slot.mobile_legibility || currentImageMeta?.mobile_legibility
          ),
        };
      });
    },

    getRecommendationForSlot(slotIndex) {
      const slotNumber = slotIndex + 1;
      const slot = this.recommendedImageOrder[slotIndex];

      return this.resolvedImageRecommendations.find((item) => {
        if (item.slot === slotNumber) {
          return true;
        }

        return item.slot_type && slot && item.slot_type === slot.slot_type;
      }) || null;
    },

    makeImageGenerationPrompt(slotIndex) {
      const slot = this.recommendedImageOrder[slotIndex];
      const recommendation = this.getRecommendationForSlot(slotIndex);

      const productName = this.resolvedProduct.name || 'the product';
      const brand = this.resolvedProduct.brand ? `${this.resolvedProduct.brand} ` : '';

      const isImprove = slot?.status === 'improve';
      const isMissing = slot?.status === 'missing';
      const slotType = slot?.slot_type || slot?.type || 'Image improvement';
      const isHeroSlot = slotType.toLowerCase().includes('hero');

      const improveGuardrails = isImprove
        ? [
          'This is a corrective edit of the provided PDP image, not a full redesign.',
          'Keep the existing composition as close as possible to the reference.',
          'Preserve the current camera angle, perspective, crop, product arrangement, background, and overall scene structure.',
          'Make only the minimum necessary visual changes required by the image-generation instruction.',
          'Do not restage, redesign, or substantially recompose the image.',
          'Do not convert the image into a new layout or a new product arrangement.',
          'Use the provided product image as the direct edit reference.',
        ]
        : [];

      const missingGuardrails = isMissing
        ? [
          'Create a new PDP carousel image for the requested missing slot.',
          'Use the provided product image only as the product accuracy reference.',
          'You may create a new clean eCommerce composition if needed for this slot.',
        ]
        : [];

      const parts = [
        `Generate a production-ready PDP image for ${brand}${productName}.`,
        '',

        `Target carousel slot: ${slotType}.`,
        slot?.intent ? `Slot intent: ${slot.intent}` : null,
        slot?.why_it_matters ? `Why it matters: ${slot.why_it_matters}` : null,
        '',

        recommendation?.label ? `Recommendation: ${recommendation.label}` : null,
        recommendation?.text ? `Creative brief: ${recommendation.text}` : null,
        '',

        // For improve, put the corrective-edit rules BEFORE the generation brief
        // so the model interprets the brief as an edit, not a redesign.
        ...improveGuardrails,
        improveGuardrails.length ? '' : null,

        // For missing slots, allow creation mode.
        ...missingGuardrails,
        missingGuardrails.length ? '' : null,

        // Highest-priority production instruction from report JSON.
        slot?.generation_brief
          ? `Primary image-generation instruction: ${slot.generation_brief}`
          : null,
        '',

        isImprove && slot?.improve_reason
          ? `Improve the existing PDP image: ${slot.improve_reason}`
          : null,
        '',

        'Preserve the same product identity, package structure, overall shape, color family, and branding cues only when they match the confirmed PDP facts.',
        'If the reference image conflicts with the confirmed PDP facts, prioritize the confirmed PDP facts and correct the visible product details accordingly.',
        'If the reference image shows a different variant, flavor, scent, style, size, pack count, net weight, or bundle structure than the confirmed PDP product, replace the conflicting detail with the confirmed PDP version.',
        'Do not preserve incorrect product text, incorrect variant names, incorrect pack facts, or conflicting visible details from the reference image.',
        '',

        isImprove
          ? 'For this edit, keep the image close to the original and correct only the conflicting or weak details.'
          : 'Create a clean eCommerce-ready image suitable for a product detail page carousel.',

        'Do not invent unsupported claims, badges, certifications, icons, stickers, or specs.',
        'Do not add external callouts unless the slot explicitly requires them.',

        isHeroSlot
          ? 'For a marketplace-style hero image, do not add external callouts. Improve readability by redrawing the visible package hierarchy on the product itself when needed.'
          : null,

        slot?.language
          ? `Image generation language: ${slot.language}. Use this language for any on-image text when text is required.`
          : null,
      ];

      return parts.filter(Boolean).join('\n');
    },

    async handleGenerate(slotIndex) {
      const slot = this.recommendedImageOrder[slotIndex];

      if (!slot) {
        return;
      }

      if (!this.isAuthorized) {
        this.notifyAuthRequired();
        return;
      }

      if (!this.isSlotClickable(slot, slotIndex)) {
        if (this.freePreviewUsed) {
          this.notify(
            "You've used your free AI preview",
            'Contact us to unlock unlimited previews or full image production.',
            'warning'
          );
        }

        return;
      }

      const sourceImage = this.getSourceImageForSlot(slotIndex);

      if (!sourceImage) {
        this.notify('No source image found', 'Cannot generate preview without a product source image.');
        return;
      }

      this.clearGenerationTimers();

      this.generatingSlot = slotIndex;
      this.generationProgress = 0;

      const progressStart = Date.now();

      this.generationTimer = window.setInterval(() => {
        const elapsed = Date.now() - progressStart;
        this.generationProgress = Math.min(0.95, elapsed / GENERATION_DURATION_MS);
      }, 200);

      try {
        const prompt = this.makeImageGenerationPrompt(slotIndex);

        const formData = new FormData();
        formData.append('layout_id', 0);
        formData.append('layout_template_id', 0);
        formData.append('function', this.selectedService);
        formData.append('prompt', prompt);
        formData.append('number_of_images', 1);
        formData.append('audience', null);
        formData.append('product_image_urls[]', sourceImage);

        const genRes = await axios.post('/banner/groups/generate_image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (genRes.data?.error || !genRes.data?.uuid) {
          throw new Error('Image generation failed to start.');
        }

        const generatedUrl = await this.pollImageGenerationJob(genRes.data.uuid);

        this.generationProgress = 1;
        this.$set(this.generatedSlots, slotIndex, generatedUrl);

        await this.saveAiImageToReport(slotIndex, generatedUrl);

        this.notify('AI preview ready', 'This is your free preview. Contact us to unlock more.');
      } catch (error) {
        console.error('Image generation failed:', error);
        this.notify('Image generation failed', error.message || 'Please try again.');
      } finally {
        this.clearGenerationTimers();
        this.generatingSlot = null;
      }
    },

    async pollImageGenerationJob(uuid) {
      const url = `/banner/groups/check_image_generation_job/${uuid}`;
      const maxAttempts = 24;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const response = await axios.get(url, { timeout: 20000 });

          if (response.data?.done) {
            const firstUrl = response.data?.image_urls?.[0];

            if (firstUrl) {
              return firstUrl;
            }

            throw new Error('Generation finished but no image URL was returned.');
          }
        } catch (error) {
          await this.sleep(5000);
          continue;
        }

        await this.sleep(5000);
      }

      throw new Error('Generation timed out. Please try again.');
    },

    async saveAiImageToReport(slotIndex, src) {
      const { id, uuid } = this.getReportIdentity();
      const nextAiImages = this.buildNextAiImages(slotIndex, src);

      if (!id || !uuid) {
        console.warn('Cannot save AI image: report id or uuid is missing');
        this.localAiImages = nextAiImages;
        return;
      }

      const formData = new FormData();
      formData.append('key', 'ai_images');
      formData.append('value', JSON.stringify(nextAiImages));

      const response = await axios.post(`/analysis-reports/${id}/${uuid}`, formData);

      const savedAiImages = response.data?.ai_images || nextAiImages;

      this.localAiImages = savedAiImages;
      this.$emit('ai-images-updated', savedAiImages);
    },

    getReportIdentity() {
      const directId = this.reportId
        || this.resolvedReportData.report_id
        || this.resolvedProduct.report_id
        || null;

      const directUuid = this.reportUuid
        || this.resolvedReportData.report_uuid
        || this.resolvedProduct.report_uuid
        || null;

      if (directId && directUuid) {
        return {
          id: directId,
          uuid: directUuid,
        };
      }

      if (window.currentReport?.id && window.currentReport?.uuid) {
        return {
          id: window.currentReport.id,
          uuid: window.currentReport.uuid,
        };
      }

      if (window.reportId && window.reportUuid) {
        return {
          id: window.reportId,
          uuid: window.reportUuid,
        };
      }

      const parts = window.location.pathname.split('/').filter(Boolean);

      const reportIndex = parts.indexOf('analysis-reports');
      if (reportIndex !== -1 && parts[reportIndex + 1] && parts[reportIndex + 2]) {
        return {
          id: parts[reportIndex + 1],
          uuid: parts[reportIndex + 2],
        };
      }

      const previewIndex = parts.indexOf('analysis-report-preview');
      if (previewIndex !== -1 && parts[previewIndex + 1] && parts[previewIndex + 2]) {
        return {
          id: parts[previewIndex + 1],
          uuid: parts[previewIndex + 2],
        };
      }

      return {
        id: null,
        uuid: null,
      };
    },

    buildNextAiImages(slotIndex, src) {
      const nextAiImages = this.localAiImages
        .filter((item) => item.index !== slotIndex)
        .map((item) => ({
          index: item.index,
          src: this.normalizeUrlWithCurrentHost(item.src),
        }));

      nextAiImages.push({
        index: slotIndex,
        src: this.normalizeUrlWithCurrentHost(src),
      });

      return nextAiImages.sort((a, b) => a.index - b.index);
    },

    normalizeUrlWithCurrentHost(src) {
      if (!src || typeof src !== 'string') {
        return src;
      }

      if (/^(https?:)?\/\//i.test(src) || /^(blob:|data:)/i.test(src)) {
        return src;
      }

      if (src.startsWith('/')) {
        return `${window.location.origin}${src}`;
      }

      return `${window.location.origin}/${src}`;
    },

    makeSlotKey(slot, index) {
      return `${slot.view_type || 'slot'}-${slot.display_index || index}-${slot.image_index !== null ? slot.image_index : 'missing'}`;
    },

    getGenerationSlotIndex(slot, displayIndex) {
      if (slot.view_type === 'recommended') {
        return displayIndex;
      }

      if (Number.isInteger(slot.recommended_slot)) {
        return Math.max(slot.recommended_slot - 1, 0);
      }

      return displayIndex;
    },

    isDisplaySlotClickable(slot, displayIndex) {
      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const recommendedSlot = this.recommendedImageOrder[generationSlotIndex] || slot;

      return this.isSlotClickable(recommendedSlot, generationSlotIndex);
    },

    isDisplaySlotGeneratable(slot, displayIndex) {
      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const recommendedSlot = this.recommendedImageOrder[generationSlotIndex] || slot;

      return this.canGenerateSlot(recommendedSlot, generationSlotIndex);
    },

    isDisplaySlotLocked(slot, displayIndex) {
      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const recommendedSlot = this.recommendedImageOrder[generationSlotIndex] || slot;

      return this.isSlotLocked(recommendedSlot, generationSlotIndex);
    },

    getDisplaySlotButtonClass(slot, displayIndex) {
      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const recommendedSlot = this.recommendedImageOrder[generationSlotIndex] || slot;
      const displaySlot = {
        ...recommendedSlot,
        ai_src: slot.ai_src || recommendedSlot.ai_src,
        status: slot.status || recommendedSlot.status,
      };

      return this.getSlotButtonClass(displaySlot, generationSlotIndex);
    },

    isSlotPresent(slot) {
      return slot.status === 'present';
    },

    isSlotImprove(slot) {
      return slot.status === 'improve';
    },

    isSlotMissing(slot) {
      return slot.status === 'missing';
    },

    isSlotLocked(slot) {
      return !slot.ai_src
        && this.isSlotMissing(slot)
        && !slot.src
        && this.freePreviewUsed;
    },

    canGenerateSlot(slot, index) {
      const canPreviewStatus = this.isSlotMissing(slot) || this.isSlotImprove(slot);

      return canPreviewStatus
        && !slot.ai_src
        && !this.generatedSlots[index]
        && this.generatingSlot !== index
        && !this.freePreviewUsed;
    },

    isSlotClickable(slot, index) {
      return this.isAuthorized && this.canGenerateSlot(slot, index);
    },

    getSlotButtonClass(slot, index) {
      if (this.generatingSlot === index) {
        return 'border-2 border-primary/40 bg-primary/5 cursor-wait';
      }

      if (slot.ai_src) {
        return 'border border-primary/40 bg-primary/5 cursor-zoom-in';
      }

      if (this.isSlotPresent(slot)) {
        return 'border border-border bg-muted/30 cursor-zoom-in';
      }

      if (this.isSlotImprove(slot)) {
        if (slot.src || slot.ai_src) {
          return 'border-2 border-amber-300 bg-amber-50/60 cursor-zoom-in';
        }

        if (this.freePreviewUsed) {
          return 'border border-dashed border-border bg-muted/20 cursor-not-allowed';
        }

        return 'border-2 border-amber-400 bg-amber-50 hover:border-amber-500 hover:bg-amber-100 cursor-pointer';
      }

      if (this.isSlotLocked(slot, index)) {
        return 'border border-dashed border-border bg-muted/20 cursor-not-allowed';
      }

      return 'border border-dashed border-primary/40 bg-primary/5 hover:border-primary hover:bg-primary/10 cursor-pointer';
    },

    sleep(ms) {
      return new Promise((resolve) => window.setTimeout(resolve, ms));
    },

    clearGenerationTimers() {
      if (this.generationTimer) {
        window.clearInterval(this.generationTimer);
        this.generationTimer = null;
      }

      if (this.generationFinishTimer) {
        window.clearTimeout(this.generationFinishTimer);
        this.generationFinishTimer = null;
      }
    },

    notify(title, message = null, type = 'success') {
      if (window.toastr) {
        const method = window.toastr[type] || window.toastr.success;
        method.call(window.toastr, message || title, message ? title : undefined);
      }
    },

    notifyAuthRequired() {
      this.notify(
        'Sign in required',
        'Please sign in to generate or improve images.',
        'warning'
      );
    },

    getSourceImageForSlot(slotIndex) {
      const slot = this.recommendedImageOrder[slotIndex];

      if (
        slot
        && slot.status === 'improve'
        && Number.isInteger(slot.image_index)
        && this.resolvedImages[slot.image_index]
      ) {
        return this.resolvedImages[slot.image_index];
      }

      return this.aiSourceImage;
    },
    normalizeMobileLegibility(value) {
      if (value === true) {
        return {
          status: 'ready',
          reason: 'Product and key details are readable at mobile thumbnail size.',
        };
      }

      if (value === false) {
        return {
          status: 'needs_work',
          reason: 'Mobile readability needs improvement.',
        };
      }

      if (typeof value === 'string') {
        const normalized = value.toLowerCase().trim();

        if (['ready', 'mobile_ready', 'pass', 'passed'].includes(normalized)) {
          return {
            status: 'ready',
            reason: 'Product and key details are readable at mobile thumbnail size.',
          };
        }

        if (['needs_work', 'need_works', 'not_ready', 'fail', 'failed', 'improve'].includes(normalized)) {
          return {
            status: 'needs_work',
            reason: 'Mobile readability needs improvement.',
          };
        }
      }

      if (value && typeof value === 'object') {
        const rawStatus = String(value.status || '').toLowerCase().trim();

        let status = 'unknown';

        if (['ready', 'mobile_ready', 'pass', 'passed'].includes(rawStatus)) {
          status = 'ready';
        } else if (['needs_work', 'need_works', 'not_ready', 'fail', 'failed', 'improve'].includes(rawStatus)) {
          status = 'needs_work';
        }

        return {
          status,
          reason: value.reason || value.tooltip || value.issue || null,
        };
      }

      return {
        status: 'unknown',
        reason: null,
      };
    },

    getMobileLegibilityData(slot) {
      return this.normalizeMobileLegibility(slot?.mobile_legibility);
    },

    getMobileLegibilityStatus(slot) {
      return this.getMobileLegibilityData(slot).status;
    },

    getMobileLegibilityLabel(slot) {
      return this.getMobileLegibilityStatus(slot) === 'ready'
        ? 'Mobile ready'
        : 'Needs mobile work';
    },

    getMobileLegibilityReason(slot) {
      const data = this.getMobileLegibilityData(slot);

      if (data.reason) {
        return data.reason;
      }

      return data.status === 'ready'
        ? 'Product and key details are easy to decode on mobile.'
        : 'This image needs stronger mobile readability.';
    },

    getMobileLegibilityBadgeClass(slot) {
      return this.getMobileLegibilityStatus(slot) === 'ready'
        ? 'mobile-legibility-badge__btn--ready'
        : 'mobile-legibility-badge__btn--needs-work';
    },

    handleImproveClick(slot, displayIndex) {
      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);

      if (!this.isAuthorized) {
        this.notifyAuthRequired();
        return;
      }

      if (this.isDisplayImproveDisabled(slot, displayIndex)) {
        return;
      }

      this.handleGenerate(generationSlotIndex);
    },

    isDisplayImproveButtonDisabled(slot, displayIndex) {
      return this.isAuthorized && this.isDisplayImproveDisabled(slot, displayIndex);
    },

    getDisplayImproveTitle(slot, displayIndex) {
      if (!this.isAuthorized) {
        return 'Sign in to generate improved preview';
      }

      return this.isDisplayImproveDisabled(slot, displayIndex)
        ? 'Free AI preview already used'
        : 'Generate improved preview';
    },

    isDisplayImproveDisabled(slot, displayIndex) {
      const generationSlotIndex = this.getGenerationSlotIndex(slot, displayIndex);
      const recommendedSlot = this.recommendedImageOrder[generationSlotIndex] || slot;

      return !this.isSlotClickable(recommendedSlot, generationSlotIndex);
    },
  },
};
</script>

<style lang="scss" scoped>
.carousel-order-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 4px;
  border: 1px solid #d9dee7;
  border-radius: 999px;
  background: #f8fafc;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.carousel-order-toggle__btn {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  outline: none;
  background: transparent;
  color: #334155;
  border-radius: 999px;
  padding: 7px 18px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  box-shadow: none;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.carousel-order-toggle__btn:hover {
  background: #eef2f7;
}

.carousel-order-toggle__btn.is-active {
  background: #ffffff;
  color: #0f172a;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.08),
    inset 0 0 0 1px #d9dee7;
}

.carousel-order-toggle__btn:focus,
.carousel-order-toggle__btn:active {
  outline: none;
  box-shadow: none;
}

.carousel-order-toggle__btn.is-active:focus,
.carousel-order-toggle__btn.is-active:active {
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.08),
    inset 0 0 0 1px #d9dee7;
}

.mobile-legibility-badge {
  position: absolute;
}

.mobile-legibility-badge__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: rgba(255, 255, 255, 0.96);
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.08),
    0 8px 18px rgba(15, 23, 42, 0.14);
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background-color 0.15s ease;
  cursor: default;
}

.mobile-legibility-badge__btn--ready {
  color: #16a34a;
}

.mobile-legibility-badge__btn--needs-work {
  color: #d97706;
}

.mobile-legibility-badge:hover .mobile-legibility-badge__btn,
.mobile-legibility-badge:focus-within .mobile-legibility-badge__btn {
  transform: translateY(-1px);
  background: #ffffff;
  box-shadow:
    0 2px 5px rgba(15, 23, 42, 0.12),
    0 10px 24px rgba(15, 23, 42, 0.18);
}

.mobile-legibility-tooltip {
  position: absolute;
  right: 0;
  bottom: 38px;
  width: 150px;
  max-width: calc(170px - 12px);
  padding: 8px 9px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.94);
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.2);
  opacity: 0;
  visibility: hidden;
  transform: translateY(4px);
  transition:
    opacity 0.15s ease,
    transform 0.15s ease,
    visibility 0.15s ease;
  pointer-events: none;
  text-align: left;
  z-index: 41;
}

.mobile-legibility-badge:hover .mobile-legibility-tooltip,
.mobile-legibility-badge:focus-within .mobile-legibility-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.mobile-legibility-tooltip__title {
  font-size: 10px;
  font-weight: 800;
  line-height: 1.25;
}

.mobile-legibility-tooltip__text {
  margin-top: 3px;
  font-size: 9px;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.84);
}

.optix-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.82);
  backdrop-filter: blur(6px);
}

.optix-lightbox__content {
  width: min(960px, 100%);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
}

.optix-lightbox__image-wrap {
  width: 100%;
  max-height: calc(100vh - 150px);
  border-radius: 18px;
  background: #ffffff;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.12);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.optix-lightbox__image {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 150px);
  width: auto;
  height: auto;
  object-fit: contain;
}

.optix-lightbox__caption {
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  padding: 12px 14px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
}

.optix-lightbox__title {
  font-size: 13px;
  font-weight: 800;
  line-height: 1.25;
}

.optix-lightbox__subtitle {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.45;
  color: #64748b;
}

.optix-lightbox__close {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 10000;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24);
  cursor: pointer;
  transition:
    transform 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.optix-lightbox__close:hover {
  transform: translateY(-1px);
  background: #ffffff;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.28);
}

.optix-lightbox-fade-enter-active,
.optix-lightbox-fade-leave-active {
  transition: opacity 0.16s ease;
}

.optix-lightbox-fade-enter,
.optix-lightbox-fade-leave-to {
  opacity: 0;
}

:global(body.optix-lightbox-open) {
  overflow: hidden;
}
</style>
